import type { Product } from "@/mocks/products";
import type { CartItem } from "@/context/CartContext";

interface NuvemshopApiProduct {
  id: number;
  name: string;
  handle: string;
  price: string | number | null;
  compareAtPrice: string | number | null;
  image: string | null;
  images: string[];
  inStock: boolean;
  categories: number[];
  variantId: number | null;
  attributes?: Record<string, string[]>;
}

export interface NuvemshopCategory {
  id: string;
  label: string;
}

const configuredApi = (import.meta.env.VITE_NUVEMSHOP_API_URL as string | undefined)?.replace(/\/$/, "");
const API_BASE = configuredApi ? (import.meta.env.DEV ? "/nuvemshop-api" : configuredApi) : undefined;

export function isNuvemshopEnabled(): boolean {
  return Boolean(API_BASE);
}

// O HostGator às vezes intercepta a primeira requisição de uma sessão com um
// desafio anti-bot (responde com um <script> que só seta um cookie e recarrega
// a página). Um fetch() nunca executa esse script, então resolvemos manualmente:
// setamos o cookie indicado e repetimos a chamada uma vez.
async function fetchPastBotChallenge(url: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(url, init);
  const text = await response.clone().text();
  const challenge = text.match(/document\.cookie\s*=\s*"([^"]+)"/);
  if (challenge) {
    document.cookie = challenge[1];
    return fetch(url, init);
  }
  return response;
}

function findAttributeValues(attributes: Record<string, string[]> | undefined, ...names: string[]): string[] {
  if (!attributes) return [];
  const lowerNames = names.map((n) => n.toLowerCase());
  for (const [key, values] of Object.entries(attributes)) {
    if (lowerNames.includes(key.toLowerCase())) return values;
  }
  return [];
}

function toProduct(item: NuvemshopApiProduct): Product {
  const price = Number(item.price ?? 0);
  const colors = findAttributeValues(item.attributes, "cor", "color");
  const materials = findAttributeValues(item.attributes, "material");
  return {
    id: item.id,
    name: item.name,
    price,
    installments: 12,
    installmentValue: price / 12,
    image: item.image ?? "",
    inStock: item.inStock,
    color: colors[0] ?? "",
    material: materials[0] ?? "",
    variantId: item.variantId ?? undefined,
    categoryIds: item.categories,
    attributes: item.attributes,
  };
}

export async function fetchNuvemshopProducts(params: { categoryId?: string; page?: number; productId?: string } = {}): Promise<Product[]> {
  if (!API_BASE) return [];

  const search = new URLSearchParams();
  if (params.productId) search.set("product_id", params.productId);
  if (params.categoryId) search.set("category_id", params.categoryId);
  if (params.page) search.set("page", String(params.page));

  const response = await fetchPastBotChallenge(`${API_BASE}/products.php?${search.toString()}`);
  if (!response.ok) {
    throw new Error(`Falha ao buscar produtos da Nuvemshop (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (!Array.isArray(data.products)) throw new Error("Resposta inválida do catálogo.");
  return (data.products as NuvemshopApiProduct[]).filter(item => !params.productId || String(item.id) === params.productId).map(toProduct);
}

export async function fetchNuvemshopCategories(): Promise<NuvemshopCategory[]> {
  if (!API_BASE) return [];

  const response = await fetchPastBotChallenge(`${API_BASE}/categories.php`);
  if (!response.ok) {
    throw new Error(`Falha ao buscar categorias da Nuvemshop (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (!Array.isArray(data.categories)) throw new Error("Resposta inválida de categorias.");
  return (data.categories as Array<{ id: number; name: string }>).map((category) => ({
    id: String(category.id),
    label: category.name,
  }));
}

export async function createNuvemshopCheckout(
  items: CartItem[],
  contact: { email: string; fullName: string }
): Promise<string> {
  if (!API_BASE) {
    throw new Error("Integração com a Nuvemshop não está configurada (VITE_NUVEMSHOP_API_URL ausente).");
  }

  if (items.length === 0 || items.some(item => !Number.isSafeInteger(item.variantId) || item.variantId! <= 0 || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || item.quantity > 100)) {
    throw new Error("Um item não está disponível para checkout. Remova-o e adicione novamente pela vitrine.");
  }
  const [contactName, ...rest] = contact.fullName.trim().split(/\s+/);
  const contactLastname = rest.length > 0 ? rest.join(" ") : contactName;

  const payload = {
    contact_email: contact.email,
    contact_name: contactName,
    contact_lastname: contactLastname,
    items: items.map((item) => ({
      variant_id: item.variantId,
      quantity: item.quantity,
    })),
  };

  const response = await fetchPastBotChallenge(`${API_BASE}/checkout.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const details = await response.json().catch(() => null);
    throw new Error(details?.message ?? `Falha ao criar o checkout (HTTP ${response.status})`);
  }

  const data = await response.json();
  if (typeof data.checkout_url !== 'string') throw new Error('O checkout não retornou um endereço de pagamento.');
  const url = new URL(data.checkout_url);
  if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Endereço de checkout inválido.');
  return url.href;
}
